import Select from '@components/v2/form/Select/Select';
import MenuItem from '@components/v2/ui/Menu/MenuItem';
import Typography from '@components/v2/ui/Typography/Typography';
import { Stack } from '@mui/material';
import { FeatureFlagEnum } from '../../../../../featureflag/feature-flag.enum';
import { useFeatureFlag } from '../../../../../hooks/useFeatureFlag';
import LangEnum from '../../../../../lang/LangEnum';
import { useTranslation } from '../../../../translation/TranslationContext';
import SettingContainer from '../../../SettingContainer';
import { ReferencePeriodEnum } from '../../../enums/referencePeriod.enum';
import { getReferencePeriodSelectLabel } from '../../../utils/getReferencePeriodSelectLabel.utils';
import { SelloutSectionGeneralSettingsProps } from '../../types/GeneralSettings.types';
import { SelloutDateRangeInput } from './components/SelloutDateRangeInput';
import { SelloutLastDaysInput } from './components/SelloutLastDaysInput';

const SelloutSectionGeneralSettings = ({
  generalSettings,
  handleChangeAndBlurList,
  handleChangeAndBlur,
  handleBlur,
  handleChange,
}: SelloutSectionGeneralSettingsProps) => {
  const { translate } = useTranslation();
  const { getFeatureFlag } = useFeatureFlag();

  if (!getFeatureFlag(FeatureFlagEnum.ENABLE_SELLOUT_IN_COMMERCE)) {
    return null;
  }

  const referencePeriodItems = Object.keys(ReferencePeriodEnum).map((size) => {
    const translation = getReferencePeriodSelectLabel(size as ReferencePeriodEnum);
    return {
      label: translate(translation.label),
      description: translate(translation.description),
      value: size,
    };
  });

  return (
    <SettingContainer
      title={translate(LangEnum.GLOBAL_SELLOUT)}
      renderInfo={() => (
        <Stack sx={{ gap: 1 }}>
          <Typography color="textSecondary">
            {translate(LangEnum.SELLOUT_GENERAL_SETTINGS_SECTION_DESCRIPTION)}
          </Typography>
          <Typography color="textSecondary">
            {translate(LangEnum.SELLOUT_GENERAL_SETTINGS_SELLOUT_PERIOD_SUB_TITLE)}
          </Typography>
        </Stack>
      )}
    >
      <Stack sx={{ gap: 2 }}>
        <Select
          name="selloutReferencePeriod"
          label={translate(LangEnum.SELLOUT_GENERAL_SETTINGS_SELLOUT_REFERENCE_PERIOD_LABEL)}
          value={generalSettings.selloutReferencePeriod}
          renderValue={(selected) =>
            referencePeriodItems.find((item) => item.value === selected)?.label ?? ''
          }
          onChange={(event) => {
            handleChangeAndBlur({
              value: (event.target.value as string) || null,
              name: 'selloutReferencePeriod',
            });
          }}
        >
          {referencePeriodItems.map((item) => (
            <MenuItem
              key={item.value}
              value={item.value}
              sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 0.5 }}
            >
              <Typography variant="body1">{item.label}</Typography>
              <Typography variant="body2" color="textSecondary">
                {item.description}
              </Typography>
            </MenuItem>
          ))}
        </Select>
        {generalSettings.selloutReferencePeriod === ReferencePeriodEnum.LAST_DAYS ? (
          <SelloutLastDaysInput
            selloutPeriodLastDaysInterval={generalSettings.selloutPeriodLastDaysInterval}
            handleBlur={handleBlur}
            handleChange={handleChange}
          />
        ) : (
          <SelloutDateRangeInput
            selloutPeriodCustomIntervalStartDate={
              generalSettings.selloutPeriodCustomIntervalStartDate
            }
            selloutPeriodCustomIntervalEndDate={generalSettings.selloutPeriodCustomIntervalEndDate}
            handleChangeAndBlurList={handleChangeAndBlurList}
          />
        )}
      </Stack>
    </SettingContainer>
  );
};

export default SelloutSectionGeneralSettings;
